import { FetchBaseQueryError, createApi } from '@reduxjs/toolkit/query/react';
import InitialLoadingManager from '../../services/api/InitialLoadingManager';
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  CompanyMenusData,
  Franchise,
  ICompanyImpressum,
  ILegalDocument,
  InitialDataResponseInterface,
  LegalDocumentSlug,
} from '../../services/exports/Interfaces';
import { fakeBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import BackendApiClient from '../../services/api/BackendApiClient';
import withLikeCounts from '../../services/menu/withLikeCounts';

interface CompanyRequestDto {
  slug: string;
  hideLoading?: boolean;
}

interface LegalDocumentRequestDto {
  slug: string;
  document: LegalDocumentSlug;
  lang?: string;
}

export enum CompaniesApiSliceTags {
  Menus = 'Menus',
}

export const companiesApiSlice = createApi({
  baseQuery: fakeBaseQuery(),
  reducerPath: 'companiesApiSlice',
  tagTypes: Object.values(CompaniesApiSliceTags),
  endpoints: (build) => ({
    getFranchiseData: build.query<QueryReturnValue<Franchise, FetchBaseQueryError>, boolean | undefined>({
      queryFn: (hideLoading = false) =>
        InitialLoadingManager.getFranchiseData(hideLoading).then((res) => {
          if (res.success) {
            return { data: res.response.data, status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    getCompanyData: build.query<InitialDataResponseInterface, CompanyRequestDto>({
      queryFn: ({ slug, hideLoading }) =>
        InitialLoadingManager.getCompanyData(slug, hideLoading).then((res) => {
          if (res.success) {
            return { data: res.response.data, status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    getFranchiseImpressums: build.query<ICompanyImpressum[], void>({
      queryFn: () =>
        InitialLoadingManager.getFranchiseImpressums().then((res) => {
          if (res.success) {
            return { data: res.response.data?.data ?? [], status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    getLegalDocument: build.query<ILegalDocument, LegalDocumentRequestDto>({
      queryFn: ({ slug, document, lang }) =>
        InitialLoadingManager.getLegalDocument(slug, document, lang).then((res) => {
          // A 200 with no payload is treated as a failure rather than cached as an
          // ILegalDocument that is actually undefined — the caller falls back either
          // way, but the cache should not record a lie for 60s.
          if (res.success && res.response.data?.data) {
            return { data: res.response.data.data, status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    getCompanyMenusData: build.query<CompanyMenusData, number>({
      queryFn: async (companyId) => {
        // Like counts have their own endpoint: the menu response is cached for days on the
        // backend, and a like changing it would force a rebuild of the whole menu.
        const [res, likes] = await Promise.all([
          BackendApiClient.request({
            method: 'GET',
            url: `/companies/${companyId}/menus`,
          }),
          BackendApiClient.request({
            method: 'GET',
            url: `/companies/${companyId}/product-likes`,
          }),
        ]);

        if (!res.success) {
          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }

        return {
          data: withLikeCounts(res.response.data?.data, likes.success ? likes.response.data?.data : null),
          status: res.status,
        };
      },
      providesTags: [CompaniesApiSliceTags.Menus],
    }),
  }),
});

export const {
  useGetFranchiseDataQuery,
  useLazyGetFranchiseDataQuery,
  useGetCompanyDataQuery,
  useGetFranchiseImpressumsQuery,
  useGetLegalDocumentQuery,
  useGetCompanyMenusDataQuery,
  useLazyGetCompanyMenusDataQuery,
} = companiesApiSlice;
